import React, { useEffect, useRef, useState } from "react";
import { Table, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import CollapsibleSection from "./CollapsibleSection";

interface DocumentsSectionProps {
  enrollmentId: number;
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({ enrollmentId }) => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [showDocuments, setShowDocuments] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [uploadRemarks, setUploadRemarks] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDocuments = () => {
    setLoading(true);
    setLoadError(null);
    EnrollmentService.getDocuments(enrollmentId)
      .then((res: any) => {
        if (res.data?.success) {
          setDocuments(res.data.data.documents || []);
          // Java gates this block on enquiry-type-2 config
          // `showDocumentsInCreateAndViewEnrollment` (default true).
          if (res.data.data.showDocuments === false) setShowDocuments(false);
        } else {
          setLoadError(res.data?.message || "Failed to load documents");
        }
      })
      .catch((err: any) =>
        setLoadError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load documents",
        ),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDocuments();
  }, [enrollmentId]);

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setUploadError("Please select a file");
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (documentName.trim()) formData.append("documentName", documentName.trim());
      if (documentNumber.trim()) formData.append("documentNumber", documentNumber.trim());
      if (uploadRemarks.trim()) formData.append("remarks", uploadRemarks.trim());

      const res = await EnrollmentService.uploadDocument(enrollmentId, formData);
      if (res.data?.success) {
        setShowUpload(false);
        setDocumentName("");
        setDocumentNumber("");
        setUploadRemarks("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        loadDocuments();
      } else {
        setUploadError(res.data?.message || "Upload failed");
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Config-driven visibility (Java hides #documents when the flag is false)
  if (!loading && !showDocuments) return null;

  return (
    <CollapsibleSection title="Documents" count={documents.length} defaultOpen={false}>
      {loading ? (
        <div className="ev-loading"><i className="fa fa-spinner fa-spin" /> Loading documents...</div>
      ) : loadError ? (
        <Alert variant="danger" style={{ fontSize: 12, marginBottom: 0 }}>
          {loadError}
        </Alert>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            <Button
              variant="primary"
              size="sm"
              style={{ fontSize: 11 }}
              onClick={() => { setShowUpload(p => !p); setUploadError(null); }}
            >
              <i className="fa fa-upload" /> Upload Document
            </Button>
          </div>

          {showUpload && (
            <div style={{ background: "var(--surface-page)", padding: 10, borderRadius: 4, marginBottom: 10, fontSize: 12 }}>
              {uploadError && (
                <Alert variant="danger" style={{ fontSize: 12, padding: "4px 10px" }}>{uploadError}</Alert>
              )}
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>File *</Form.Label>
                <Form.Control type="file" size="sm" ref={fileInputRef as any} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Document Name</Form.Label>
                <Form.Control
                  type="text" size="sm"
                  value={documentName}
                  onChange={e => setDocumentName(e.target.value)}
                  placeholder="e.g. Birth Certificate"
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Document Number</Form.Label>
                <Form.Control
                  type="text" size="sm"
                  value={documentNumber}
                  onChange={e => setDocumentNumber(e.target.value)}
                  placeholder="e.g. ABC123456"
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Remarks</Form.Label>
                <Form.Control
                  as="textarea" rows={2} size="sm"
                  value={uploadRemarks}
                  onChange={e => setUploadRemarks(e.target.value)}
                />
              </Form.Group>
              <div style={{ display: "flex", gap: 6 }}>
                <Button variant="primary" size="sm" style={{ fontSize: 11 }} onClick={handleUpload} disabled={uploading}>
                  {uploading ? <><Spinner role="status" animation="border" size="sm" /> Uploading...</> : "Upload"}
                </Button>
                <Button variant="secondary" size="sm" style={{ fontSize: 11 }} onClick={() => setShowUpload(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {documents.length === 0 ? (
            <div className="ev-empty">No documents</div>
          ) : (
            <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Type</th>
                  <th>Number</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Required</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d: any) => (
                  <tr key={d.id}>
                    <td>
                      {d.filePath ? (
                        <a href={d.filePath} target="_blank" rel="noreferrer" title="View document">
                          {d.documentName || "Document"} <i className="fa fa-external-link" style={{ fontSize: 10 }} />
                        </a>
                      ) : (
                        d.documentName || "Document"
                      )}
                    </td>
                    <td>{d.documentType}</td>
                    <td>{d.documentNumber}</td>
                    <td>{d.uploadedDate}</td>
                    <td>
                      <span
                        className="ev-status-badge"
                        style={{
                          background: d.status === 1 ? "var(--status-success-bg)" : "var(--status-warning-bg)",
                          color: d.status === 1 ? "var(--status-success)" : "var(--status-warning)",
                        }}
                      >
                        {d.status === 1 ? "Verified" : d.status === 0 ? "Pending" : d.status || "—"}
                      </span>
                    </td>
                    <td>{d.isRequired ? "Yes" : "No"}</td>
                    <td>{d.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </CollapsibleSection>
  );
};

export default DocumentsSection;
